import React, { useState } from 'react';
import { Button } from 'src/components/ui/Button';

interface EditOrgDetailsModalProps {
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    type: string;
    description?: string;
  }) => Promise<void>;
  initialData: {
    name: string;
    type: string;
    description?: string;
  };
}

export const EditOrgDetailsModal: React.FC<EditOrgDetailsModalProps> = ({
  onClose,
  onSubmit,
  initialData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    type: initialData.type || 'other',
    description: initialData.description || ''
  });

  const handleSubmit = async () => {
    setModalError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({
        name: formData.name.trim(),
        type: formData.type,
        description: formData.description.trim() || undefined
      });
      onClose();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to update organization details');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setModalError(null);
  };

  const isFormValid = () => {
    return (
      formData.name.trim().length > 0 &&
      formData.type.length > 0
    );
  };

  const hasChanges = () => {
    return (
      formData.name !== initialData.name ||
      formData.type !== initialData.type ||
      formData.description !== (initialData.description || '')
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl font-semibold">Edit Organization Details</h2>
          <button onClick={handleClose} className="text-white/40 hover:text-white">✕</button>
        </div>

        {modalError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {modalError}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-white text-sm font-medium mb-2">Organization Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Acme Records"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30"
            />
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">Organization Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white/30"
            >
              <option value="record_label">Record Label</option>
              <option value="publisher">Publisher</option>
              <option value="management">Management</option>
              <option value="production">Production</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">Description (Optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of your organization..."
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleClose} variant="ghost" className="flex-1">Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="default"
            className="flex-1"
            disabled={!isFormValid() || !hasChanges() || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};